/* eslint-disable react-hooks/rules-of-hooks */
// We are disabling this conditional hook warning warning because we have a condition we need to handle.
// If teams are not allowed, we should not attempt to load any team info.
import {
  useTeamInfo,
  useTeamPhoto,
  useTeamParticipants,
  useParticipantsPhotos,
  useGetCaptainsMessage,
} from '@frx/frx-react-context';
import { useTeamsAllowed } from './use-teams-allowed';
import { useTeamId } from './use-teamId';

import { TeamMemberProps, getTeamMembers, PhotoResponse } from '../utils';

/**
 * @description A hook that encapsulates all the team information.
 *   When teams are not supported, the team fields are not populated
 *   and teamsAllowed = false is returned
 *
 * @param eventId event id number
 */
export const useTeams = (eventId: number) => {
  const teamsAllowed = useTeamsAllowed();

  // Teams are not supported, so don't bother loading any team info.
  if (!teamsAllowed) return { teamsAllowed: false };

  const teamId = useTeamId();
  const teamData = useTeamInfo({ fr_id: eventId, team_id: teamId });
  const teamInfo = teamData.data;

  const teamPhotoResponse = useTeamPhoto({
    fr_id: eventId,
    cons_id: teamInfo?.captainConsId,
  });
  const teamPhoto = teamPhotoResponse?.data;

  const { data: teams, isInitialLoading: isLoadingTeams } = useTeamParticipants(
    {
      fr_id: eventId,
      team_id: teamId,
      list_page_size: 500,
    }
  );

  const participantsIds = (teams?.participant || []).map(
    ({ consId }: { consId: number }) => consId
  );

  const photos = useParticipantsPhotos(participantsIds);

  const teamMembers: TeamMemberProps[] = getTeamMembers(
    teams,
    photos as PhotoResponse[]
  );

  const { data: captainsMessageData } = useGetCaptainsMessage({
    fr_id: eventId,
  });

  const captainsMessage = captainsMessageData?.message || '';

  return {
    teamsAllowed,
    teamId,
    isLoadingTeams,
    teamPhoto,
    teamMembers,
    teamInfo,
    teams,
    teamData,
    captainsMessage,
  };
};
